/* eslint-disable @typescript-eslint/no-unused-vars */
import { STPlaceholderType } from '@tencent/slide-sdk-pb-model';
import { ORPHAN_INDEX } from '../constants/shape-constants';
import {
    createLayoutPlaceholderData,
    LayoutPlaceholderOptionsMap,
    AddPlaceholderType,
    PLACEHOLDER_TYPE_MAP,
    createMasterPlaceholderData,
    MasterPlaceholderOptionsMap,
} from '../data/data-factory/placeholder-factory';
import { cloneDeep } from '../utils/clone-deep';
import { record } from '../utils/recorder';
import { DataTypeUtils, type ILayoutDataDelta } from '../data';
import { SetPagePropertiesMutation } from '../mutation/page/set-page-properties-mutation';
import { PageMutationAPI } from './page-mutation-api';
import type { IPlaceholderShapeDataModel } from '../model/group-shape-data-model';
import type { LayoutDataModel } from '../model/layout-data-model';
import type { ICTExtendedGuide, ICTHeaderFooterPmlDelta } from '@tencent/slide-sdk-pb-model';
import type { ShapeDataModel } from '../model/shape-data-model';
import type { LayoutUniquePlaceholderType } from '../data/data-factory/placeholder-factory';

export class LayoutMutationAPI extends PageMutationAPI<LayoutDataModel> {
    /**
     * 解除 slide 占位符和 layout 某一占位符的关联关系
     *
     * 1. 找到与 layout 目标占位符相关联的 slide 占位符
     * 2. 更新 slide 占位符的 idx
     * 3. 更新 slide 占位符的 继承属性
     * @param parentPlaceholder layout 目标占位符
     */
    @record()
    public unlinkMatchedShapesOfPlaceholder(parentPlaceholder: ShapeDataModel) {
        this.transaction(() => {
            this.model.context.getPresentation().slideList.forEach((slide) => {
                if (this.model.id !== slide.getLayout()?.id) return;
                const updateShape = slide.placeholders.find((shape) =>
                    this.model.store.isMatchedPlaceholder(shape.props.nvPr.ph, parentPlaceholder.props.nvPr.ph),
                );
                if (updateShape) {
                    updateShape.api.updateProps({
                        nvPr: { ph: { idx: ORPHAN_INDEX } },
                        spPr: { prstGeom: { prst: parentPlaceholder.props.spPr.prstGeom?.prst } },
                    });
                    const parentFrame = parentPlaceholder.getInheritedData().spPr?.xfrm;
                    if (parentFrame) {
                        updateShape.api.updateTransform(cloneDeep(parentFrame));
                    }
                }
            });
        });
    }

    /**
     * 建立 slide 占位符和 layout 某一占位符的关联关系
     *
     * 1. 找到可以与 layout 目标占位符建立关联的 slide 占位符
     * 2. 更新 slide 占位符的 idx：和 layout 目标占位符保持一致
     * @param parentPlaceholder layout 目标占位符
     */
    @record()
    public linkMatchedShapesOfPlaceholder(parentPlaceholder: ShapeDataModel) {
        this.transaction(() => {
            this.model.context.getPresentation().slideList.forEach((slide) => {
                if (this.model.id !== slide.getLayout()?.id) return;
                const updateShape = slide.placeholders.find((shape) =>
                    this.canLinkPlaceholders(shape, parentPlaceholder),
                );
                if (updateShape) {
                    updateShape.api.updateProps({ nvPr: { ph: { idx: parentPlaceholder.props.nvPr.ph?.idx } } });
                }
            });
        });
    }

    /**
     * 添加版式的占位符:
     * 这些占位符同一版式可以添加多个
     */
    @record()
    public addPlaceholder(placeholderType: Exclude<AddPlaceholderType, LayoutUniquePlaceholderType>, isVert = false) {
        const newIndex = this.createPlaceholderIndex();
        const type = placeholderType === AddPlaceholderType.Content ? undefined : PLACEHOLDER_TYPE_MAP[placeholderType];
        this.addShapes([
            createLayoutPlaceholderData({
                ...LayoutPlaceholderOptionsMap[placeholderType],
                isVert,
                placeholderIndex: newIndex,
            }),
        ]);
        return this.model.store.findPlaceholderShape({ idx: newIndex, type });
    }

    /**
     * 添加版式的唯一占位符:
     * 这些占位符同一版式只能添加一个（标题、日期、页脚、幻灯片编号）
     * 唯一占位符的初始样式固定，和母版保持一致
     * 其中标题占位符没有 idx（和 office 保持一致）
     */
    @record()
    public addUniquePlaceholder(placeholderType: LayoutUniquePlaceholderType) {
        const placeholderDataType = PLACEHOLDER_TYPE_MAP[placeholderType];
        const placeholderIndex =
            placeholderType === AddPlaceholderType.Title ? undefined : this.createPlaceholderIndex();
        // master 中没有父级占位符的时候需要根据 master 的样式添加占位符
        const useMasterPlaceholder = !this.model.getMaster()?.getPlaceholderShape({ type: placeholderDataType });
        if (useMasterPlaceholder) {
            this.addShapes([
                createMasterPlaceholderData({
                    ...MasterPlaceholderOptionsMap[placeholderType],
                    placeholderIndex,
                }),
            ]);
        } else {
            this.addShapes([
                createLayoutPlaceholderData({
                    ...LayoutPlaceholderOptionsMap[placeholderType],
                    placeholderIndex,
                }),
            ]);
        }
        return this.model.store.findPlaceholderShape({ idx: placeholderIndex, type: placeholderDataType });
    }

    /**
     * 是否显示母版上的图形
     * @param showMasterSp
     */
    @record()
    public updateShowMasterSp(showMasterSp: boolean) {
        this.updateProps<ILayoutDataDelta>({ showMasterSp });
    }

    @record()
    public updateHeaderFooter(hf: ICTHeaderFooterPmlDelta) {
        this.updateProps<ILayoutDataDelta>({ hf });
    }

    @record()
    public updateSlideGuideList(slideGuideList: ICTExtendedGuide[], canUndo = false) {
        this.transaction(() => {
            const mut = new SetPagePropertiesMutation({
                pageId: this.model.id,
                pageType: DataTypeUtils.toSTPageType(this.model.type),
                layoutProperties: {
                    slideGuideList: {
                        guide: slideGuideList.map((guide, index) => ({ ...guide, id: `${index + 1}` })),
                    },
                },
            });
            mut.invertible = canUndo;
            this.request([mut]);
        });
    }

    /**
     * 已知两个 placeholder，判断是否可以建立关联
     * 占位符可以根据特定的继承关系恢复继承父级。
     *
     * @param childShape 待建立关联的子占位符
     * @param parentShape 待建立关联的父占位符
     */
    private canLinkPlaceholders(
        childShape: IPlaceholderShapeDataModel,
        parentShape: IPlaceholderShapeDataModel,
    ): boolean {
        const { idx } = childShape.props.nvPr.ph ?? {};
        const childType = childShape.props.nvPr.ph?.type ?? STPlaceholderType.PBDEFAULT;
        const parentType = parentShape.props.nvPr.ph?.type ?? STPlaceholderType.PBDEFAULT;
        const title = [STPlaceholderType.title, STPlaceholderType.ctrTitle];
        const bottom = [STPlaceholderType.dt, STPlaceholderType.ftr, STPlaceholderType.sldNum];
        const idxIsOrphan = idx === ORPHAN_INDEX;
        // title 类型的占位符可以相互匹配
        if (title.includes(childType)) {
            return title.includes(parentType) && idxIsOrphan;
        }
        // 页脚占位符需要类型精准匹配
        if (bottom.includes(childType)) {
            return childType === parentType && idxIsOrphan;
        }
        return false;
    }
}
